import { FormikProps } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidationError, ValidationErrorResponse } from 'types/api';
import { RequestCardFormValues } from 'components/modals/RequestCard/formData';

export class ActivateCardError extends Error {
  internalCodes: string[];

  constructor(
    public type: 'billing' | 'invite' | 'card',
    public innerError: ValidationErrorResponse
  ) {
    super('Card request failed');
    this.internalCodes = innerError.details?.map((c) => c.code);
  }

  static Billing(innerError: ValidationErrorResponse) {
    return new ActivateCardError('billing', innerError);
  }
}

export function useActivateCardErrorFormatter() {
  const { t } = useTranslation();

  return useCallback(
    (
      error: ValidationError,
      formikProps: FormikProps<RequestCardFormValues> | null
    ) => {
      if (!formikProps) {
        return false;
      }
      switch (error.code) {
        case 'BILLING_PAYMENT_METHOD_NOT_ALLOWED_BY_PROVIDER': {
          formikProps.setFieldError(
            'billing.paymentMethod',
            t(
              'billing.paymentMethodNotAllowedByProvider',
              'The selected payment method is not allowed by the payment provider. Please choose another one.'
            )
          );
          break;
        }
        case 'SIGNUP_INVALID_IBAN':
          formikProps.setFieldError(
            'billing.ibanNumber',
            t('updateBillingInfo.errors.invalidIban', 'Invalid IBAN number')
          );
          break;
        case 'SIGNUP_INVALID_BIC':
          formikProps.setFieldError(
            'billing.bicNumber',
            t('updateBillingInfo.errors.invalidBic', 'Invalid BIC number')
          );
          break;
        case 'SIGNUP_INVALID_POSTAL_CODE_FOR_COUNTRY':
          formikProps.setFieldError(
            'billing.postalCode',
            t(
              'updateBillingInfo.errors.invalidPostalCode',
              'Invalid postal code'
            )
          );
          break;
        case 'SIGNUP_INVALID_COUNTRY':
          formikProps.setFieldError(
            'billing.countryCode',
            t(
              'updateBillingInfo.errors.invalidCountryCode',
              'Please select a supported country'
            )
          );
          break;
        case 'SIGNUP_VAT_INVALID_FOR_COUNTRY':
          formikProps.setFieldError(
            'billing.vatNumber',
            t(
              'updateBillingInfo.errors.invalidVatNumber',
              'Invalid for the selected country'
            )
          );
          break;
        case 'SIGNUP_COC_INVALID_FOR_COUNTRY':
          formikProps.setFieldError(
            'billing.chamberOfCommerceNumber',
            t(
              'updateBillingInfo.errors.invalidVatNumber',
              'Invalid for the selected country'
            )
          );
          break;
        default:
          console.error('Unhandled request card error', error);
          return false;
      }
      return true;
    },
    [t]
  );
}
